'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from '@spikemark/ui/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@spikemark/ui/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel } from '@spikemark/ui/components/ui/form';
import { FC, ReactNode, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormAccessProvider, FormAccessControl as FormControl } from '@/components/form/form-access';
import { LogoUpload } from '@/components/form/logo-upload';
import { FormErrors } from '@/components/form/form-errors';
import { SpikemarkResource } from '@spikemark/auth';
import { Input } from '@spikemark/ui/components/design-system/form/input';
import CountrySelector from '@spikemark/ui/components/design-system/form/country-selector';
import { Button } from '@spikemark/ui/components/design-system/button';
import { TeamPlayerFormData } from './types';

// School year options from existing codebase
const SCHOOL_YEAR_OPTIONS = ['First Year', 'Second Year', 'Third Year', 'Fourth Year', 'Fifth Year', 'Sixth Year'];
const PlayerProfileFormSchema = z.object({
  userId: z.string().optional(),
  player_image: z.string().optional(),
  fullName: z.string().trim().min(1, 'Full name is required'),
  display_name: z.string().trim().min(1, 'Display name is required'),
  name_pronunciation: z.string().optional(),
  nationality: z.string().optional(),
  email: z.string().trim().min(1, 'Email is required').email('Invalid email format'),
  school_year: z.string().optional()
});
const PlayerProfileFormLegacySchema = z.object({
  userId: z.string().optional(),
  playerImage: z.string().optional(),
  fullName: z.string().trim().min(1, 'Full name is required'),
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  displayName: z.string().trim().min(1, 'Display name is required'),
  namePronunciation: z.string().optional(),
  nationality: z.string().optional(),
  schoolEmail: z.string().trim().min(1, 'Email is required').email('Invalid email format'),
  schoolYear: z.string().optional()
});
export type PlayerProfileFormData = z.infer<typeof PlayerProfileFormSchema>;
export type PlayerProfileFormLegacyData = z.infer<typeof PlayerProfileFormLegacySchema>;
interface PlayerProfileFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TeamPlayerFormData) => void | Promise<void>;
  defaultValues?: Partial<PlayerProfileFormData>;
  isSubmitting?: boolean;
  title: string;
  description: ReactNode;
}
export const PlayerProfileForm: FC<PlayerProfileFormProps> = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  isSubmitting = false,
  title,
  description
}) => {
  const form = useForm<TeamPlayerFormData>({
    resolver: zodResolver(PlayerProfileFormSchema),
    defaultValues: {
      player_image: '',
      fullName: '',
      display_name: '',
      name_pronunciation: '',
      nationality: '',
      email: '',
      school_year: '',
      ...defaultValues
    },
    mode: 'onSubmit'
  });
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      onClose();
    }
  }, [onClose]);
  const handleSubmit = useCallback(async (data: TeamPlayerFormData) => {
    await onSubmit(data);
  }, [onSubmit]);
  const handleNameBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const {
      value
    } = event.currentTarget;
    const {
      fullName,
      display_name
    } = form.getValues();
    const hasFullName = fullName && fullName.trim() !== '';
    const firstName = hasFullName ? fullName.split(' ')[0] : '';
    const lastName = hasFullName ? fullName.split(' ').slice(1).join(' ') : '';
    const hasFirstName = firstName && firstName.trim() !== '';
    const hasLastName = lastName && lastName.trim() !== '';

    // Auto-generate display name if not already set
    if (value && !display_name && hasFirstName && hasLastName) {
      form.setValue('display_name', `${firstName.charAt(0)}.${lastName}`);
    }
  }, [form]);
  return <Dialog open={open} modal onOpenChange={handleOpenChange} data-sentry-element="Dialog" data-sentry-component="PlayerProfileForm" data-sentry-source-file="player-profile-form.tsx">
      <DialogContent className="overflow-auto lg:inset-0 lg:left-[50%] lg:top-[50%] lg:min-h-80vh" data-sentry-element="DialogContent" data-sentry-source-file="player-profile-form.tsx">
        <FormAccessProvider resource={SpikemarkResource.Players} action="update" data={{}} data-sentry-element="FormAccessProvider" data-sentry-source-file="player-profile-form.tsx">
          <form>
            <Form {...form} data-sentry-element="Form" data-sentry-source-file="player-profile-form.tsx">
              <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="player-profile-form.tsx">
                <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="player-profile-form.tsx">{title}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="py-4 space-y-4 text-gray-600 " data-sentry-element="DialogDescription" data-sentry-source-file="player-profile-form.tsx">
                {description}

                {/* Photo Upload Section */}
                <FormField name="playerImage" render={({
                field
              }) => <FormItem>
                      <FormControl>
                        <LogoUpload altText="Player Photo" mode="player" {...field} />
                      </FormControl>
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />

                <div className="flex space-x-4 flex-1 justify-between">
                  <FormField name="fullName" render={({
                  field
                }) => <FormItem className="flex-1">
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter player's first name" onBlur={handleNameBlur} />
                        </FormControl>
                      </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />
                </div>

                {/* Display Name */}
                <FormField name="display_name" render={({
                field
              }) => <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., J.Smith" />
                      </FormControl>
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />

                {/* Name Pronunciation */}
                <FormField name="name_pronunciation" render={({
                field
              }) => <FormItem>
                      <FormLabel>Name Pronunciation</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="E.g. “LEE-zuh” or “Muh-HA-med”" />
                      </FormControl>
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />

                {/* Nationality */}
                <FormField name="nationality" render={({
                field
              }) => <FormItem>
                      <FormLabel>Nationality</FormLabel>
                      <FormControl>
                        <CountrySelector className="w-full" value={field.value || ''} onChange={value => form.setValue('nationality', value)} />
                      </FormControl>
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />

                {/* School Email Address */}
                {!defaultValues?.userId ? <FormField name="email" render={({
                field
              }) => <FormItem>
                        <FormLabel>School Email Address</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="player@university.edu" />
                        </FormControl>
                      </FormItem>} /> : null}

                {/* School Year */}
                <FormField name="school_year" render={({
                field
              }) => <FormItem>
                      <FormLabel>School Year</FormLabel>
                      <FormControl>
                        <Combobox inDialog className="w-full" options={SCHOOL_YEAR_OPTIONS} placeholder="Select school year" onChange={value => form.setValue('school_year', value)} value={field.value || ''} />
                      </FormControl>
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="player-profile-form.tsx" />

                <FormErrors errors={form.formState.errors} data-sentry-element="FormErrors" data-sentry-source-file="player-profile-form.tsx" />
              </DialogDescription>

              <DialogFooter className="flex justify-between" data-sentry-element="DialogFooter" data-sentry-source-file="player-profile-form.tsx">
                <Button variant="ghost" type="button" onClick={() => {
                handleOpenChange(false);
                handleSubmit(form.getValues());
              }} disabled={isSubmitting} data-sentry-element="Button" data-sentry-source-file="player-profile-form.tsx">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || form.formState.isSubmitting} variant="primary" data-sentry-element="Button" data-sentry-source-file="player-profile-form.tsx">
                  {isSubmitting || form.formState.isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </Form>
          </form>
        </FormAccessProvider>
      </DialogContent>
    </Dialog>;
};